import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { PageHeader } from '../../components/PageHeader';
import { NimbusTable } from '../../components/NimbusTable';
import { SortableTh } from '../../components/SortableTh';
import { useSortableData } from '../../hooks/useSortableData';
import { subprocessorsApi, processingActivityApi, aiUseCaseApi, assetsApi } from '../../services/api';
import type { Asset, DataField } from '../../types/models';
import { DATA_CATEGORY_LABELS } from '../../constants/privacy';

// Subprocessor has no shared type in models.ts (a deliberate choice made when the register
// was built - see SubprocessorDetail.tsx) so this mirrors just the fields this page reads.
interface SubprocessorLite {
  subprocessorId: string;
  name: string;
  systemIds?: string[];
  dataFields?: DataField[];
}

interface ProcessingActivityLite {
  activityId: string;
  name: string;
  systemIds?: string[];
  dataFields?: DataField[];
}

interface AiUseCaseLite {
  useCaseId: string;
  name: string;
  dataFields?: DataField[];
}

type SourceKind = 'Subprocessor' | 'Processing Activity' | 'AI Use Case';

// 'Subprocessor' stays the SourceKind's internal value (used as an object key throughout this
// file) - only its DISPLAY name changed when the register was renamed to "Controllers &
// Processors". Any user-visible rendering of a SourceKind should go through this map, not the
// raw value.
const SOURCE_KIND_LABEL: Record<SourceKind, string> = {
  Subprocessor: 'Controller / Processor',
  'Processing Activity': 'Processing Activity',
  'AI Use Case': 'AI Use Case',
};

interface Contribution {
  kind: SourceKind;
  id: string;
  name: string;
  path: string;
  systemIds: string[];
  dataFields: DataField[];
}

const UNLINKED = '__unlinked__';

interface SystemRow {
  systemId: string;
  systemName: string;
  isRealSystem: boolean;
  dataTypeNames: string[];
  hasSpecialCategory: boolean;
  bySourceKind: Record<SourceKind, number>;
}

interface DataTypeRow {
  name: string;
  categories: string[];
  isSpecialCategory: boolean;
  contributions: { kind: SourceKind; id: string; name: string; path: string; systemNames: string[] }[];
}

const Dash: React.FC = () => <span style={{ color: 'var(--bs-secondary-color)' }}>—</span>;

const TagList: React.FC<{ values: string[]; max?: number }> = ({ values, max = 3 }) => {
  if (values.length === 0) return <Dash />;
  const shown = values.slice(0, max);
  const rest = values.length - shown.length;
  return (
    <span className="d-flex flex-wrap gap-1">
      {shown.map((v) => (
        <span key={v} className="np-chip np-tone-neutral">{v}</span>
      ))}
      {rest > 0 && <span className="np-chip np-tone-neutral">+{rest}</span>}
    </span>
  );
};

const recordedViaText = (counts: Record<SourceKind, number>): React.ReactNode => {
  const parts: string[] = [];
  // 'Subprocessor' is the SourceKind key (unchanged, used as an object key elsewhere in this
  // file) - the display name for that register is now "Controllers & Processors", a compound
  // phrase that doesn't pluralize cleanly, so this uses a generic noun instead of trying to.
  if (counts.Subprocessor) parts.push(`${counts.Subprocessor} vendor record${counts.Subprocessor > 1 ? 's' : ''}`);
  if (counts['Processing Activity']) parts.push(`${counts['Processing Activity']} Processing Activit${counts['Processing Activity'] > 1 ? 'ies' : 'y'}`);
  if (counts['AI Use Case']) parts.push(`${counts['AI Use Case']} AI Use Case${counts['AI Use Case'] > 1 ? 's' : ''}`);
  if (parts.length === 0) return <Dash />;
  return <span className="np-sub">{parts.join(' · ')}</span>;
};

type SystemSortField = 'systemName' | 'dataTypeCount' | 'hasSpecialCategory' | 'recordedVia';
type TypeSortField = 'name' | 'systemCount' | 'contributionCount' | 'isSpecialCategory';

export const DataInventoryPage: React.FC = () => {
  const [view, setView] = useState<'system' | 'type'>('system');

  const { data: subprocessors = [], isLoading: loadingSubprocessors } = useQuery<SubprocessorLite[]>({
    queryKey: ['subprocessors'],
    queryFn: subprocessorsApi.getAll,
  });
  const { data: activities = [], isLoading: loadingActivities } = useQuery<ProcessingActivityLite[]>({
    queryKey: ['processing-activities'],
    queryFn: processingActivityApi.list,
  });
  const { data: useCases = [], isLoading: loadingUseCases } = useQuery<AiUseCaseLite[]>({
    queryKey: ['ai-use-cases'],
    queryFn: aiUseCaseApi.list,
  });
  const { data: assets = [], isLoading: loadingAssets } = useQuery<Asset[]>({
    queryKey: ['assets'],
    queryFn: assetsApi.getAll,
  });

  const isLoading = loadingSubprocessors || loadingActivities || loadingUseCases || loadingAssets;

  const contributions: Contribution[] = useMemo(() => {
    const out: Contribution[] = [];
    subprocessors.forEach((s) => {
      if ((s.dataFields || []).length === 0) return;
      out.push({
        kind: 'Subprocessor', id: s.subprocessorId, name: s.name, path: `/subprocessors/${s.subprocessorId}`,
        systemIds: s.systemIds || [], dataFields: s.dataFields || [],
      });
    });
    activities.forEach((a) => {
      if ((a.dataFields || []).length === 0) return;
      out.push({
        kind: 'Processing Activity', id: a.activityId, name: a.name, path: `/processing-activities/${a.activityId}`,
        systemIds: a.systemIds || [], dataFields: a.dataFields || [],
      });
    });
    useCases.forEach((u) => {
      if ((u.dataFields || []).length === 0) return;
      // AI Use Cases don't link to a system today (see the Subprocessors Register Concept's
      // "Being honest about precision" section) - every one of these lands in the unlinked
      // bucket in the by-system view, and shows up with no system in the by-type view.
      out.push({
        kind: 'AI Use Case', id: u.useCaseId, name: u.name, path: `/ai-use-cases/${u.useCaseId}`,
        systemIds: [], dataFields: u.dataFields || [],
      });
    });
    return out;
  }, [subprocessors, activities, useCases]);

  const assetNameById = useMemo(() => {
    const map = new Map<string, string>();
    assets.forEach((a) => map.set(a.assetId, a.name));
    return map;
  }, [assets]);

  const { systemRows, distinctDataTypeCount, systemsWithSpecialCategory, systemsWithNoInventory, unlinkedCount } = useMemo(() => {
    const bySystem = new Map<string, {
      names: Set<string>; special: boolean; counts: Record<SourceKind, number>; recordedIds: Record<SourceKind, Set<string>>;
    }>();
    const ensure = (systemId: string) => {
      let row = bySystem.get(systemId);
      if (!row) {
        row = {
          names: new Set(), special: false,
          counts: { Subprocessor: 0, 'Processing Activity': 0, 'AI Use Case': 0 },
          recordedIds: { Subprocessor: new Set(), 'Processing Activity': new Set(), 'AI Use Case': new Set() },
        };
        bySystem.set(systemId, row);
      }
      return row;
    };

    const allTypeNames = new Set<string>();
    contributions.forEach((c) => {
      const targets = c.systemIds.length > 0 ? c.systemIds : [UNLINKED];
      targets.forEach((systemId) => {
        const row = ensure(systemId);
        c.dataFields.forEach((f) => {
          row.names.add(f.name);
          allTypeNames.add(f.name);
          if (f.isSpecialCategory) row.special = true;
        });
        if (!row.recordedIds[c.kind].has(c.id)) {
          row.recordedIds[c.kind].add(c.id);
          row.counts[c.kind] += 1;
        }
      });
    });

    const rows: SystemRow[] = [];
    bySystem.forEach((row, systemId) => {
      if (systemId === UNLINKED) return;
      rows.push({
        systemId,
        systemName: assetNameById.get(systemId) || systemId,
        isRealSystem: true,
        dataTypeNames: Array.from(row.names),
        hasSpecialCategory: row.special,
        bySourceKind: row.counts,
      });
    });

    const linkedSystemIds = new Set(rows.map((r) => r.systemId));
    const noInventory = assets.filter((a) => !linkedSystemIds.has(a.assetId));

    const unlinked = bySystem.get(UNLINKED);

    return {
      systemRows: rows,
      distinctDataTypeCount: allTypeNames.size,
      systemsWithSpecialCategory: rows.filter((r) => r.hasSpecialCategory).length,
      systemsWithNoInventory: noInventory.length,
      unlinkedCount: unlinked ? unlinked.names.size : 0,
    };
  }, [contributions, assetNameById, assets]);

  const dataTypeRows: DataTypeRow[] = useMemo(() => {
    const byType = new Map<string, DataTypeRow>();
    contributions.forEach((c) => {
      const systemNames = c.systemIds.map((id) => assetNameById.get(id) || id);
      c.dataFields.forEach((f) => {
        let row = byType.get(f.name);
        if (!row) {
          row = { name: f.name, categories: [], isSpecialCategory: false, contributions: [] };
          byType.set(f.name, row);
        }
        if (!row.categories.includes(f.category)) row.categories.push(f.category);
        if (f.isSpecialCategory) row.isSpecialCategory = true;
        if (!row.contributions.some((existing) => existing.kind === c.kind && existing.id === c.id)) {
          row.contributions.push({ kind: c.kind, id: c.id, name: c.name, path: c.path, systemNames });
        }
      });
    });
    return Array.from(byType.values());
  }, [contributions, assetNameById]);

  const systemValueFor = (row: SystemRow, field: SystemSortField): string | number => {
    if (field === 'systemName') return row.systemName;
    if (field === 'dataTypeCount') return row.dataTypeNames.length;
    if (field === 'hasSpecialCategory') return row.hasSpecialCategory ? 1 : 0;
    return row.bySourceKind.Subprocessor + row.bySourceKind['Processing Activity'] + row.bySourceKind['AI Use Case'];
  };
  const { sorted: sortedSystemRows, field: systemField, dir: systemDir, toggle: toggleSystemSort } =
    useSortableData(systemRows, systemValueFor, 'dataTypeCount' as SystemSortField, 'desc');

  const typeValueFor = (row: DataTypeRow, field: TypeSortField): string | number => {
    if (field === 'name') return row.name;
    if (field === 'systemCount') return new Set(row.contributions.flatMap((c) => c.systemNames)).size;
    if (field === 'isSpecialCategory') return row.isSpecialCategory ? 1 : 0;
    return row.contributions.length;
  };
  const { sorted: sortedTypeRows, field: typeField, dir: typeDir, toggle: toggleTypeSort } =
    useSortableData(dataTypeRows, typeValueFor, 'contributionCount' as TypeSortField, 'desc');

  const recordedByCounts = useMemo(() => {
    const counts: Record<SourceKind, number> = { Subprocessor: 0, 'Processing Activity': 0, 'AI Use Case': 0 };
    const seen: Record<SourceKind, Set<string>> = { Subprocessor: new Set(), 'Processing Activity': new Set(), 'AI Use Case': new Set() };
    contributions.forEach((c) => {
      if (!seen[c.kind].has(c.id)) {
        seen[c.kind].add(c.id);
        counts[c.kind] += 1;
      }
    });
    return counts;
  }, [contributions]);

  return (
    <div className="container-xxl flex-grow-1 container-p-y np-page">
      <PageHeader
        icon="ri-database-2-line"
        title="Data Inventory"
        subtitle="What personal data we hold, and where — assembled from Controllers & Processors, Processing Activities and AI Use Cases, no separate data entry."
      />

      <div className="np-grid np-grid-4">
        <div className="np-card">
          <div className="np-card-body np-stat">
            <span className="np-stat-icon np-tone-primary"><i className="ri-shapes-line"></i></span>
            <p className="np-stat-label">Distinct Data Types Recorded</p>
            <p className="np-stat-value np-tabular">{isLoading ? '—' : distinctDataTypeCount}</p>
          </div>
        </div>
        <div className="np-card">
          <div className="np-card-body np-stat">
            <span className="np-stat-icon np-tone-danger"><i className="ri-alert-line"></i></span>
            <p className="np-stat-label">Systems with Special-Category Data</p>
            <p className="np-stat-value np-tabular">{isLoading ? '—' : systemsWithSpecialCategory}</p>
          </div>
        </div>
        <div className="np-card">
          <div className="np-card-body np-stat">
            <span className="np-stat-icon np-tone-warning"><i className="ri-question-line"></i></span>
            <p className="np-stat-label">Systems with No Inventory Yet</p>
            <p className="np-stat-value np-tabular">{isLoading ? '—' : systemsWithNoInventory}</p>
            <p className="np-hint mb-0">of {assets.length} assets</p>
          </div>
        </div>
        <div className="np-card">
          <div className="np-card-body np-stat">
            <span className="np-stat-icon np-tone-info"><i className="ri-links-line"></i></span>
            <p className="np-stat-label">Records Contributing</p>
            <p className="np-stat-value" style={{ fontSize: '0.95rem' }}>
              {isLoading ? '—' : `${recordedByCounts.Subprocessor} · ${recordedByCounts['Processing Activity']} · ${recordedByCounts['AI Use Case']}`}
            </p>
            <p className="np-hint mb-0">Controllers & Processors · Processing Activities · AI Use Cases</p>
          </div>
        </div>
      </div>

      {!isLoading && unlinkedCount > 0 && (
        <div className="np-banner np-tone-warning">
          <i className="ri-error-warning-line"></i>
          <span>
            {unlinkedCount} data type{unlinkedCount > 1 ? 's are' : ' is'} recorded on a Controller/Processor, Processing Activity
            or AI Use Case with no system linked yet — shown below under "Not linked to a system". Open the record and
            set its systems to place it correctly. AI Use Cases can't be linked to a system at all yet — that's a
            separate, larger change, tracked but not built.
          </span>
        </div>
      )}

      <div className="np-tabs">
        <button type="button" className={`np-tab ${view === 'system' ? 'active' : ''}`} onClick={() => setView('system')}>
          <i className="ri-server-line me-2"></i>By system
        </button>
        <button type="button" className={`np-tab ${view === 'type' ? 'active' : ''}`} onClick={() => setView('type')}>
          <i className="ri-shapes-line me-2"></i>By data type
        </button>
      </div>

      {view === 'system' && (
        <div className="np-card">
          <div className="np-card-head">
            <h2>Personal data by system</h2>
          </div>
          <div className="np-card-body p-0">
            {isLoading ? (
              <div className="text-center py-5"><div className="spinner-border text-primary" role="status" /></div>
            ) : (
              <NimbusTable
                thead={
                  <tr>
                    <SortableTh field={'systemName' as SystemSortField} active={systemField === 'systemName'} dir={systemDir} onSort={toggleSystemSort}>System</SortableTh>
                    <th>Data types recorded</th>
                    <SortableTh field={'hasSpecialCategory' as SystemSortField} active={systemField === 'hasSpecialCategory'} dir={systemDir} onSort={toggleSystemSort} width="180px">Special category?</SortableTh>
                    <SortableTh field={'recordedVia' as SystemSortField} active={systemField === 'recordedVia'} dir={systemDir} onSort={toggleSystemSort} width="220px">Recorded via</SortableTh>
                  </tr>
                }
                isEmpty={sortedSystemRows.length === 0 && unlinkedCount === 0}
                emptyState={<div className="text-center np-sub py-4">No data inventory recorded yet.</div>}
              >
                {sortedSystemRows.map((row) => (
                  <tr key={row.systemId}>
                    <td><Link to={`/assets/${row.systemId}/edit`}>{row.systemName}</Link></td>
                    <td><TagList values={row.dataTypeNames} /></td>
                    <td>
                      {row.hasSpecialCategory
                        ? <span className="np-chip np-tone-danger">Yes</span>
                        : <span className="np-chip np-tone-neutral">No</span>}
                    </td>
                    <td>{recordedViaText(row.bySourceKind)}</td>
                  </tr>
                ))}
                {unlinkedCount > 0 && (
                  <tr>
                    <td><span className="np-sub" style={{ fontStyle: 'italic' }}>Not linked to a system</span></td>
                    <td><TagList values={Array.from(new Set(contributions.filter((c) => c.systemIds.length === 0).flatMap((c) => c.dataFields.map((f) => f.name))))} /></td>
                    <td>
                      {contributions.some((c) => c.systemIds.length === 0 && c.dataFields.some((f) => f.isSpecialCategory))
                        ? <span className="np-chip np-tone-danger">Yes</span>
                        : <span className="np-chip np-tone-neutral">No</span>}
                    </td>
                    <td>{recordedViaText((() => {
                      const counts: Record<SourceKind, number> = { Subprocessor: 0, 'Processing Activity': 0, 'AI Use Case': 0 };
                      contributions.filter((c) => c.systemIds.length === 0).forEach((c) => { counts[c.kind] += 1; });
                      return counts;
                    })())}</td>
                  </tr>
                )}
              </NimbusTable>
            )}
          </div>
        </div>
      )}

      {view === 'type' && (
        <div className="np-card">
          <div className="np-card-head">
            <h2>Systems and records by data type</h2>
          </div>
          <div className="np-card-body p-0">
            {isLoading ? (
              <div className="text-center py-5"><div className="spinner-border text-primary" role="status" /></div>
            ) : (
              <NimbusTable
                thead={
                  <tr>
                    <SortableTh field={'name' as TypeSortField} active={typeField === 'name'} dir={typeDir} onSort={toggleTypeSort}>Data type</SortableTh>
                    <th>Category</th>
                    <SortableTh field={'isSpecialCategory' as TypeSortField} active={typeField === 'isSpecialCategory'} dir={typeDir} onSort={toggleTypeSort} width="150px">Special category?</SortableTh>
                    <SortableTh field={'contributionCount' as TypeSortField} active={typeField === 'contributionCount'} dir={typeDir} onSort={toggleTypeSort} width="120px">Records</SortableTh>
                    <th>Where it shows up</th>
                  </tr>
                }
                isEmpty={sortedTypeRows.length === 0}
                emptyState={<div className="text-center np-sub py-4">No data inventory recorded yet.</div>}
              >
                {sortedTypeRows.map((row) => (
                  <tr key={row.name}>
                    <td className="fw-semibold">{row.name}</td>
                    <td>
                      <span className="d-flex flex-wrap gap-1">
                        {row.categories.map((c) => (
                          <span key={c} className="np-chip np-tone-neutral">{DATA_CATEGORY_LABELS[c as keyof typeof DATA_CATEGORY_LABELS] || c}</span>
                        ))}
                      </span>
                    </td>
                    <td>
                      {row.isSpecialCategory
                        ? <span className="np-chip np-tone-danger">Yes</span>
                        : <span className="np-chip np-tone-neutral">No</span>}
                    </td>
                    <td className="np-tabular">{row.contributions.length}</td>
                    <td>
                      <span className="d-flex flex-column gap-1">
                        {row.contributions.map((c) => (
                          <span key={`${c.kind}-${c.id}`} className="np-sub">
                            <Link to={c.path}>{c.name}</Link>
                            <span className="np-chip np-tone-info ms-2">{SOURCE_KIND_LABEL[c.kind]}</span>
                            {c.systemNames.length > 0 && <span className="ms-2">{c.systemNames.join(', ')}</span>}
                          </span>
                        ))}
                      </span>
                    </td>
                  </tr>
                ))}
              </NimbusTable>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DataInventoryPage;
